<template>
  <div class="flex flex-col p-3">
    <!-- Tabs -->
    <div class="border-b border-gray-200 mb-3">
      <nav class="flex space-x-6 px-4" aria-label="Tabs">
        <span
          :class="[tab === 'processes' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="selectProcessesTab"
        >
          Processes
        </span>
        <span
          :class="[tab === 'retry' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="tab = 'retry'"
        >
          Retry Sync
        </span>
      </nav>
    </div>

    <!-- Processes tab -->
    <div v-if="tab === 'processes'">
      <div class="flex items-center gap-3 py-2 flex-wrap">
        <input
          v-model="search"
          type="text"
          placeholder="Search"
          class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-48"
          @keyup.enter="resetAndFetch"
        />
        <input
          v-model="bnrKey"
          type="text"
          placeholder="Instrument no key"
          class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-44"
          @keyup.enter="resetAndFetch"
        />
        <select
          v-model="status"
          class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          @change="resetAndFetch"
        >
          <option value="">-- Filter by BNR Status --</option>
          <option value="EDMS_FAILED">EDMS Failed</option>
          <option value="EDMS_PENDING">EDMS Pending</option>
          <option value="SUCCESS">Success</option>
          <option value="FAILED">Failed</option>
          <option value="PENDING">Pending</option>
          <option value="SENT">Sent</option>
        </select>
        <input
          v-model="startDate"
          type="date"
          class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          @change="resetAndFetch"
        />
        <input
          v-model="endDate"
          type="date"
          class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          @change="resetAndFetch"
        />
        <select
          v-model="perPage"
          class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          @change="resetAndFetch"
        >
          <option :value="12">12</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
          :disabled="loading"
          @click="resetFilters"
        >
          <i class="fa fa-undo mr-1"></i> Reset
        </button>
      </div>

      <div>
        <table>
          <thead>
          <tr>
            <th>#</th>
            <th>Instrument No</th>
            <th>Tracking Number</th>
            <th>Document Type</th>
            <th>Instrument No Key</th>
            <th>BNR Status</th>
            <th>Created At</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="loading">
            <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
              <td v-for="j in 8" :key="j">
                <div class="h-3 bg-gray-200 rounded"></div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="(row, idx) in stream" :key="rowKey(row, idx)">
              <td>{{ (currentPage - 1) * perPage + idx + 1 }}</td>
              <td class="font-bold font-mono">{{ row.instrumentNo || "—" }}</td>
              <td class="font-mono">{{ row.trackingNumber || "—" }}</td>
              <td>{{ formatEnumLabel(row.documentType) }}</td>
              <td>{{ formatEnumLabel(row.instrumentNoKey) }}</td>
              <td>
                <span :class="statusPill(row.bnrStatus)">{{ row.bnrStatus || "—" }}</span>
              </td>
              <td class="text-xs whitespace-nowrap">{{ row.createdAt || "—" }}</td>
              <td>
                <div class="flex gap-1 flex-wrap">
                  <button
                    v-if="canRetrySyncItem(row)"
                    type="button"
                    class="btn-sm-primary"
                    :disabled="retryingRowId === resolveSyncItemId(row) || replacingId === resolveSyncItemId(row)"
                    @click="retryFromRow(row, idx)"
                  >
                    <i
                      class="fa mr-1"
                      :class="retryingRowId === resolveSyncItemId(row) ? 'fa-spinner fa-spin' : 'fa-redo'"
                    ></i>
                    Retry
                  </button>
                  <button
                    v-if="isReplaceSyncItemVisible(row)"
                    type="button"
                    class="bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50"
                    :disabled="replacingId === resolveSyncItemId(row) || retryingRowId === resolveSyncItemId(row)"
                    @click="openReplace(row)"
                  >
                    <i
                      class="fa mr-1"
                      :class="replacingId === resolveSyncItemId(row) ? 'fa-spinner fa-spin' : 'fa-exchange-alt'"
                    ></i>
                    Replace
                  </button>
                  <span
                    v-if="!canRetrySyncItem(row) && !isReplaceSyncItemVisible(row)"
                    class="text-xs text-gray-400"
                  >
                    —
                  </span>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && stream.length === 0">
            <td colspan="8" class="text-center py-6 text-gray-400 text-sm">No results found</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="pagination.totalPages > 1"
        class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2"
      >
        <p class="text-xs text-gray-500">
          Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
          <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span>
          <span class="text-gray-400 ml-2">({{ pagination.totalElements }} total)</span>
        </p>
        <div class="flex gap-2">
          <button class="btn-sm-primary" :disabled="currentPage === 1 || loading" @click="previous">
            <i class="fa fa-chevron-left mr-1"></i> Prev
          </button>
          <button class="btn-sm-primary" :disabled="currentPage === pagination.totalPages || loading" @click="next">
            Next <i class="fa fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>

    <AppModal v-model="replaceModalOpen" size="md" title="Replace Document Attachment" hide-footer>
      <form class="space-y-4 text-sm p-1" @submit.prevent="submitReplace">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Instrument No</p>
            <p class="font-mono text-xs break-all">{{ replaceRow?.instrumentNo || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Document Type</p>
            <p class="break-words">{{ formatEnumLabel(replaceRow?.documentType ?? null) }}</p>
          </div>
        </div>
        <div>
          <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Replacement file</label>
          <input
            ref="replaceFileInput"
            type="file"
            required
            class="mt-1 block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
            :disabled="replacingId != null"
            @change="onReplaceFileChange"
          />
        </div>
        <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700"
            :disabled="replacingId != null"
            @click="closeReplace"
          >
            Cancel
          </button>
          <button type="submit" class="btn-primary" :disabled="replacingId != null || !replaceFile">
            <i class="fa mr-1" :class="replacingId != null ? 'fa-spinner fa-spin' : 'fa-exchange-alt'"></i>
            {{ replacingId != null ? "Replacing..." : "Replace" }}
          </button>
        </div>
      </form>
    </AppModal>

    <!-- Retry Sync tab -->
    <div v-if="tab === 'retry'" class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Retry Legal Document Sync</h2>
        <p class="text-sm text-gray-500 mt-1">
          If a legal document failed to sync its attachments, enter the instrument number below to
          trigger a manual retry. The system will attempt to reprocess and push the document attachments.
        </p>
      </div>

      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <form @submit.prevent="retryFromForm" class="space-y-4">
          <div class="flex flex-col gap-1">
              <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Document Instrument Number
              </label>
              <input
                v-model.trim="instrumentNumber"
                type="text"
                placeholder="e.g. INS-2024-000123"
                required
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              />
              <p class="text-xs text-gray-400 mt-0.5">
                Enter the exact instrument number as registered in the legal documents system.
              </p>
          </div>

          <div class="border-t border-gray-100 pt-2 flex justify-end">
              <button type="submit" class="btn-primary" :disabled="retryLoading">
                <i class="fa fa-redo mr-1.5" :class="{ 'fa-spin': retryLoading }"></i>
                Retry Sync
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { useAbility } from "@casl/vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useConfirmDialog, useToast } from "@/composables";
import {
  canReplaceSyncItem,
  canRetrySyncItem,
  extractErrorMessage,
  fetchLegalDocumentSyncItems,
  formatEnumLabel,
  replaceLegalDocumentSyncItem,
  resolveInstrumentNumber,
  resolveSyncItemId,
  retryLegalDocumentAttachments,
  retryLegalDocumentSyncItem,
  type LegalDocumentSyncItem,
} from "./legalDocumentsSyncApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();
const { can } = useAbility();

const canViewMenuItem = (permission: string) => can(permission, "general") === true;

const tab = ref("processes");
const loading = ref(false);
const retryLoading = ref(false);
const retryingRowId = ref<string | null>(null);
const replaceModalOpen = ref(false);
const replaceRow = ref<LegalDocumentSyncItem | null>(null);
const replaceFile = ref<File | null>(null);
const replaceFileInput = ref<HTMLInputElement | null>(null);
const replacingId = ref<string | null>(null);
const instrumentNumber = ref("");
const currentPage = ref(1);
const perPage = ref(20);
const search = ref("");
const bnrKey = ref("");
const status = ref("");
const startDate = ref<string | null>(null);
const endDate = ref<string | null>(null);
const stream = ref<LegalDocumentSyncItem[]>([]);
const pagination = reactive({ totalElements: 0, totalPages: 0 });

const statusPill = (value: string | null) => {
  const base = "text-xs px-2 py-0.5 rounded-full ";
  const normalized = (value || "").toUpperCase();
  if (normalized === "SUCCESS") return base + "bg-green-50 text-green-700";
  if (normalized === "EDMS_FAILED" || normalized === "FAILED") return base + "bg-red-50 text-red-700";
  if (normalized === "EDMS_PENDING" || normalized === "PENDING") return base + "bg-blue-50 text-blue-700";
  return base + "bg-gray-100 text-gray-600";
};

const rowKey = (row: LegalDocumentSyncItem, idx: number) => {
  if (row.id) return row.id;
  const instrument = resolveInstrumentNumber(row);
  if (instrument) return instrument;
  return `legal-sync-${idx}`;
};

const isReplaceSyncItemVisible = (row: LegalDocumentSyncItem) => {
  if (!canViewMenuItem("can_replace_legal_document_attachment")) return false;
  return canReplaceSyncItem(row);
};

const fetch = async () => {
  loading.value = true;
  try {
    const result = await fetchLegalDocumentSyncItems({
      page: currentPage.value,
      perPage: perPage.value,
      search: search.value,
      bnrKey: bnrKey.value,
      status: status.value,
      startDate: startDate.value,
      endDate: endDate.value,
    });
    stream.value = result.content;
    pagination.totalElements = result.totalElements;
    pagination.totalPages = result.totalPages;
  } catch (error: unknown) {
    stream.value = [];
    pagination.totalElements = 0;
    pagination.totalPages = 0;
    errorToast("Error", extractErrorMessage(error, "Could not load legal document sync records"));
  } finally {
    loading.value = false;
  }
};

const selectProcessesTab = () => {
  tab.value = "processes";
  fetch();
};

const resetAndFetch = () => {
  currentPage.value = 1;
  fetch();
};

const resetFilters = () => {
  search.value = "";
  bnrKey.value = "";
  status.value = "";
  startDate.value = null;
  endDate.value = null;
  perPage.value = 20;
  currentPage.value = 1;
  fetch();
};

const retrySyncByInstrument = async (value: string) => {
  const instrumentNo = value.trim();
  if (!instrumentNo) {
    errorToast("Error", "Instrument number is required");
    return;
  }

  retryLoading.value = true;
  try {
    const response = await retryLegalDocumentAttachments(instrumentNo);
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Legal document sync retry submitted successfully";
    successToast("Success", message);
    instrumentNumber.value = "";
    if (tab.value === "processes") {
      await fetch();
    }
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not retry legal document sync"));
  } finally {
    retryLoading.value = false;
  }
};

const retrySyncItem = async (row: LegalDocumentSyncItem, idx: number) => {
  const syncItemId = resolveSyncItemId(row);
  if (!syncItemId) {
    errorToast("Error", "Sync item ID is missing for this record");
    return;
  }

  const label = row.instrumentNo || row.trackingNumber || syncItemId;
  const confirmed = await showConfirm(
    `Retry sync for instrument no ${label}?`,
    {
      title: "Retry Sync Item",
      okTitle: "Retry",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  const rowId = resolveSyncItemId(row);
  if (!rowId) return;
  retryingRowId.value = rowId;
  try {
    const response = await retryLegalDocumentSyncItem(syncItemId);
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Sync item retry submitted successfully";
    successToast("Success", message);
    await fetch();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not retry sync item"));
  } finally {
    retryingRowId.value = null;
  }
};

const retryFromForm = () => {
  retrySyncByInstrument(instrumentNumber.value);
};

const retryFromRow = (row: LegalDocumentSyncItem, idx: number) => {
  retrySyncItem(row, idx);
};

const openReplace = (row: LegalDocumentSyncItem) => {
  if (!isReplaceSyncItemVisible(row)) return;
  replaceRow.value = row;
  replaceFile.value = null;
  if (replaceFileInput.value) replaceFileInput.value.value = "";
  replaceModalOpen.value = true;
};

const closeReplace = () => {
  if (replacingId.value != null) return;
  replaceModalOpen.value = false;
  replaceRow.value = null;
  replaceFile.value = null;
  if (replaceFileInput.value) replaceFileInput.value.value = "";
};

const onReplaceFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  replaceFile.value = target.files?.[0] ?? null;
};

const submitReplace = async () => {
  const row = replaceRow.value;
  const file = replaceFile.value;
  const syncItemId = row ? resolveSyncItemId(row) : null;

  if (!canViewMenuItem("can_replace_legal_document_attachment")) {
    errorToast("Error", "You do not have permission to replace document attachments");
    return;
  }

  if (!row || !syncItemId) {
    errorToast("Error", "Sync item is missing");
    return;
  }
  if (!file) {
    warningToast("Validation", "Select a replacement file");
    return;
  }

  replacingId.value = syncItemId;
  try {
    const response = await replaceLegalDocumentSyncItem(syncItemId, file);
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Document attachment replaced successfully";
    successToast("Success", message);
    replaceModalOpen.value = false;
    replaceRow.value = null;
    replaceFile.value = null;
    if (replaceFileInput.value) replaceFileInput.value.value = "";
    await fetch();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not replace document attachment"));
  } finally {
    replacingId.value = null;
  }
};

const next = () => {
  if (currentPage.value < pagination.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());
</script>
